import { Search, X } from "lucide-react";
import {
  type ComponentProps,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";

import { Input } from "@/components/ui/input";
import useDebounce from "@/hooks/state/useDebounce";
import { cn } from "@/utils/core/generalUtils";

type SearchInputValue = ComponentProps<typeof Input>["value"];

interface SearchInputProps extends Omit<
  ComponentProps<typeof Input>,
  "onChange"
> {
  onChange: (searchTerm?: string) => void;
  onExit?: () => void;
}

const resolveSearchTerm = (searchTerm: SearchInputValue): string => {
  if (typeof searchTerm === "string") {
    return searchTerm;
  }

  return searchTerm ? String(searchTerm) : "";
};

/**
 * Debounced search input with escape-to-close and enter-to-prevent-submit support.
 *
 * @param {SearchInputProps} props - Search input configuration.
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the underlying input element.
 * @returns {JSX.Element} Rendered search input with optional close button.
 */
const DataTableSearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (props, ref) => {
    const {
      className,
      value: controlledSearchTerm,
      onExit,
      onChange,
      autoFocus,
      placeholder,
      ...restProps
    } = props;

    const [inputValue, setInputValue] = useState(() =>
      resolveSearchTerm(controlledSearchTerm),
    );
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const onChangeRef = useRef(onChange);
    const lastEmittedSearchTermRef = useRef(
      resolveSearchTerm(controlledSearchTerm),
    );
    const debouncedValue = useDebounce(inputValue, 500);

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
      if (event.key === "Escape" && onExit) {
        onExit();
      }
      if (event.key === "Enter") {
        event.preventDefault();
      }
    };

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      const nextSearchTerm = debouncedValue;

      if (
        !hasUserInteracted ||
        debouncedValue !== inputValue ||
        nextSearchTerm === lastEmittedSearchTermRef.current
      ) {
        return;
      }

      lastEmittedSearchTermRef.current = nextSearchTerm;
      onChangeRef.current(nextSearchTerm);
    }, [debouncedValue, hasUserInteracted, inputValue]);

    useEffect(() => {
      const nextSearchTerm = resolveSearchTerm(controlledSearchTerm);

      setInputValue(nextSearchTerm);
      lastEmittedSearchTermRef.current = nextSearchTerm;
    }, [controlledSearchTerm]);

    return (
      <div className='relative'>
        <Input
          {...restProps}
          {...(autoFocus && { autoFocus: true })}
          ref={ref}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Search"}
          icon={{
            icon: Search,
            position: "left",
          }}
          className={cn("h-9", onExit && "pr-10", className)}
          onChange={(event) => {
            setInputValue(event.target.value);
            setHasUserInteracted(true);
          }}
          value={inputValue}
        />
        {onExit && (
          <X
            onClick={onExit}
            className='absolute z-10 -translate-y-1/2 cursor-pointer size-4 top-1/2 right-3'
          />
        )}
      </div>
    );
  },
);

DataTableSearchInput.displayName = "SearchInput";

export default DataTableSearchInput;
